import React, { useRef, useEffect, useState, useCallback } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';

/**
 * Props for the ToasterNotice component.
 * @property title - The title displayed at the top of the notice.
 * @property content - The main content to display in the notice body.
 * @property onWatch - Callback when the Watch button is clicked. If provided, Watch button is displayed.
 * @property onClose - Callback when the notice is closed.
 * @property onPrev - Callback for navigating to the previous item. If provided, Previous button is displayed.
 * @property onNext - Callback for navigating to the next item. If provided, Next button is displayed.
 * @property hasPrev - Whether there is a previous item available. Only used if onPrev is provided.
 * @property hasNext - Whether there is a next item available. Only used if onNext is provided.
 */
export interface ToasterNoticeProps {
  title: string;
  content: React.ReactNode;
  onWatch?: () => void;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
}

const ANIMATION_DURATION = 150;

/**
 * ToasterNotice - A bottom-anchored drawer/toaster for displaying contextual information.
 * Provides a consistent popover experience across charts and other components.
 */
export function ToasterNotice({
  title,
  content,
  onWatch,
  onClose,
  onPrev,
  onNext,
  hasPrev = true,
  hasNext = true,
}: ToasterNoticeProps): React.JSX.Element {
  const watchButtonRef = useRef<HTMLButtonElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Handle close with animation
  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setIsVisible(false);
    // Clear any existing timeout before creating a new one
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      onClose();
    }, ANIMATION_DURATION);
  }, [isClosing, onClose]);

  // Cleanup close timeout on unmount
  useEffect(() => {
    return (): void => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Trigger animation on mount
  useEffect(() => {
    // Small delay to ensure the initial state is rendered before animating
    const timer = requestAnimationFrame(() => setIsVisible(true));
    return (): void => cancelAnimationFrame(timer);
  }, []);

  // Handle Escape key to dismiss drawer
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return (): void => document.removeEventListener('keydown', handleEscape);
  }, [handleClose]);

  // Auto-focus Watch button when drawer opens (if present)
  useEffect(() => {
    if (onWatch && watchButtonRef.current) {
      watchButtonRef.current.focus();
    }
  }, [onWatch]);

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 z-50"
        onClick={handleClose}
        aria-hidden="true"
        style={{
          backgroundColor: isVisible ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0)',
          transition: `background-color ${ANIMATION_DURATION}ms ease-out`,
        }}
      />
      {/* Drawer */}
      <div
        className="fixed bottom-0 left-1/2 z-[51] rounded-t-2xl border-t border-border p-4 pb-6 shadow-elevation-lg"
        style={{
          backgroundColor: 'var(--card)',
          width: '100%',
          maxWidth: '480px',
          transform: isVisible
            ? 'translateX(-50%) translateY(0)'
            : 'translateX(-50%) translateY(100%)',
          transition: `transform ${ANIMATION_DURATION}ms ease-out`,
        }}
      >
        {/* Drawer handle */}
        <div className="flex justify-center mb-3">
          <div
            className="w-10 h-1 rounded-full"
            style={{ backgroundColor: 'var(--border)' }}
          />
        </div>
        {/* Navigation buttons */}
        {(onPrev || onNext) && (
          <div className="flex justify-between items-center mb-4">
            {onPrev ? (
              <button
                type="button"
                onClick={onPrev}
                disabled={!hasPrev}
                aria-label="Go to previous segment"
                className="flex items-center gap-1 text-sm font-medium text-high-emphasis-font disabled:opacity-40 disabled:cursor-not-allowed disabled:!bg-transparent"
              >
                <ArrowLeft size={16} aria-hidden="true" />
                Previous
              </button>
            ) : (
              <div />
            )}
            {onNext ? (
              <button
                type="button"
                onClick={onNext}
                disabled={!hasNext}
                aria-label="Go to next segment"
                className="flex items-center gap-1 text-sm font-medium text-high-emphasis-font disabled:opacity-40 disabled:cursor-not-allowed disabled:!bg-transparent"
              >
                Next
                <ArrowRight size={16} aria-hidden="true" />
              </button>
            ) : (
              <div />
            )}
          </div>
        )}
        {/* Content */}
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-high-emphasis-font">
              {title}
            </span>
            {content}
          </div>
          {onWatch && (
            <button
              type="button"
              ref={watchButtonRef}
              onClick={onWatch}
              className="flex items-center gap-2 px-4 py-2 rounded-[var(--radius)] text-sm font-medium text-high-emphasis-font bg-transparent hover:bg-surface-100"
              style={{ border: '1px solid var(--border)' }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
              Watch
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export default ToasterNotice;
